import { useStore, type NodeProps, type Node } from '@xyflow/react'
import { memo, useEffect, useRef, useState } from 'react'

import { DESIGNER_ICON_SET } from '@flowx/designer/shared/designer-icons'

import { DirectionVertical } from '../../../dictionary/geometry.types'
import { GraphColors } from '../../../bpmn-graph.constants'
import { SwimlaneNodeData } from '../bpmn-graph.mapper'
import { useCssZoom } from '../css-zoom.context'
import { sizedIconSvg } from '../node-visuals'
import { useSwimlaneUi } from '../swimlane-ui.context'

const ICON_SIZE = 24

function SwimlaneActionButton({
  icon,
  label,
  testId,
  disabled,
  onClick,
}: {
  icon: string
  label: string
  testId: string
  disabled: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      data-testid={testId}
      disabled={disabled}
      onClick={(event) => {
        event.stopPropagation()
        onClick()
      }}
      style={{
        width: ICON_SIZE,
        height: ICON_SIZE,
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'transparent',
        border: 'none',
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'default' : 'pointer',
      }}
      dangerouslySetInnerHTML={{ __html: sizedIconSvg(DESIGNER_ICON_SET[icon] ?? '', ICON_SIZE) }}
    />
  )
}

const MIN_LANE_HEIGHT = 150
const MIN_LANE_WIDTH = 300

type ResizeEdge = 'top' | 'bottom' | 'left' | 'right'
type LaneLayout = { x: number; y: number; width: number; height: number }

const RESIZE_STRIPS: { edge: ResizeEdge; style: React.CSSProperties }[] = [
  { edge: 'top', style: { top: -4, left: 0, right: 0, height: 8, cursor: 'ns-resize' } },
  { edge: 'bottom', style: { bottom: -4, left: 0, right: 0, height: 8, cursor: 'ns-resize' } },
  { edge: 'left', style: { left: -4, top: 0, bottom: 0, width: 8, cursor: 'ew-resize' } },
  { edge: 'right', style: { right: -4, top: 0, bottom: 0, width: 8, cursor: 'ew-resize' } },
]

const INDICATOR_STYLES: Record<ResizeEdge, React.CSSProperties> = {
  top: { top: -1, left: 0, right: 0, height: 2 },
  bottom: { bottom: -1, left: 0, right: 0, height: 2 },
  left: { left: -1, top: 0, bottom: 0, width: 2 },
  right: { right: -1, top: 0, bottom: 0, width: 2 },
}

function badgePlacement(edge: ResizeEdge, zoom: number): React.CSSProperties {
  switch (edge) {
    case 'top':
      return {
        top: 12,
        left: '50%',
        transform: `translateX(-50%) scale(${1 / zoom})`,
        transformOrigin: 'top center',
      }
    case 'bottom':
      return {
        bottom: 12,
        left: '50%',
        transform: `translateX(-50%) scale(${1 / zoom})`,
        transformOrigin: 'bottom center',
      }
    case 'left':
      return {
        left: 12,
        top: '50%',
        transform: `translateY(-50%) scale(${1 / zoom})`,
        transformOrigin: 'center left',
      }
    case 'right':
      return {
        right: 12,
        top: '50%',
        transform: `translateY(-50%) scale(${1 / zoom})`,
        transformOrigin: 'center right',
      }
  }
}

function SwimlaneNode({ data }: NodeProps<Node<SwimlaneNodeData>>) {
  const { swimlane } = data
  const {
    selectedLaneId,
    readonly,
    orderedLaneIds,
    resizeBounds,
    selectLane,
    addLane,
    moveLane,
    deleteLane,
    renameLane,
    resizeLane,
  } = useSwimlaneUi()
  const [editingName, setEditingName] = useState<string | null>(null)
  const nameInputRef = useRef<HTMLInputElement>(null)
  const isRenaming = editingName !== null
  const [previewLayout, setPreviewLayout] = useState<LaneLayout | null>(null)
  const [activeEdge, setActiveEdge] = useState<ResizeEdge | null>(null)
  const [hoveredEdge, setHoveredEdge] = useState<ResizeEdge | null>(null)
  const resizeStart = useRef<{
    edge: ResizeEdge
    pointer: { x: number; y: number }
    layout: LaneLayout
  } | null>(null)
  // Committed layout kept alive in `previewLayout` while the resize service
  // round-trips (see endResize). This ref snapshots the pre-resize props so we
  // can tell when the store echoes back and it's safe to hand rendering back.
  const preResizeLayout = useRef<LaneLayout | null>(null)
  const layout = previewLayout ?? swimlane.layoutOptions

  useEffect(() => {
    if (isRenaming) {
      nameInputRef.current?.focus()
    }
  }, [isRenaming])

  // Drop `previewLayout` once the incoming swimlane.layoutOptions diverges
  // from the pre-resize snapshot — that's the store update from the resize
  // service landing (~1 network round-trip after endResize). Comparing to the
  // snapshot (not to `previewLayout`) tolerates the server clamping to a value
  // different from finalLayout, so the bridge never gets stuck.
  useEffect(() => {
    if (!previewLayout || !preResizeLayout.current) {
      return
    }
    const { x, y, width, height } = swimlane.layoutOptions
    const snapshot = preResizeLayout.current
    if (
      x !== snapshot.x ||
      y !== snapshot.y ||
      width !== snapshot.width ||
      height !== snapshot.height
    ) {
      preResizeLayout.current = null
      setPreviewLayout(null)
    }
  }, [swimlane.layoutOptions, previewLayout])
  const { width, height } = layout
  const selected = selectedLaneId === swimlane.id
  const laneIndex = orderedLaneIds.indexOf(swimlane.id)
  // The lane actions are chrome that must keep a fixed size regardless of logical
  // canvas zoom. React Flow's compensated PiP zoom is physical, so remove the
  // ancestor CSS zoom before counter-scaling.
  const physicalZoom = useStore((state) => state.transform[2])
  const cssZoom = useCssZoom()
  const logicalZoom = physicalZoom / cssZoom
  const bounds = resizeBounds.get(swimlane.id)

  // Edge drags clamp against the node bounds: own nodes bound the
  // top/bottom edges, ALL nodes bound left/right (x/width sync pool-wide on
  // commit). Lanes without nodes have ±Infinity bounds → only the minimums apply.
  const clampLayout = (edge: ResizeEdge, start: LaneLayout, dx: number, dy: number): LaneLayout => {
    switch (edge) {
      case 'bottom': {
        const minHeight = Math.max(MIN_LANE_HEIGHT, (bounds?.ownMaxY ?? -Infinity) - start.y)

        return { ...start, height: Math.max(minHeight, start.height + dy) }
      }
      case 'top': {
        const maxDelta = Math.min(
          (bounds?.ownMinY ?? Infinity) - start.y,
          start.height - MIN_LANE_HEIGHT
        )
        const delta = Math.min(dy, maxDelta)

        return { ...start, y: start.y + delta, height: start.height - delta }
      }
      case 'right': {
        const minWidth = Math.max(MIN_LANE_WIDTH, (bounds?.allMaxX ?? -Infinity) - start.x)

        return { ...start, width: Math.max(minWidth, start.width + dx) }
      }
      case 'left': {
        const maxDelta = Math.min(
          (bounds?.allMinX ?? Infinity) - start.x,
          start.width - MIN_LANE_WIDTH
        )
        const delta = Math.min(dx, maxDelta)

        return { ...start, x: start.x + delta, width: start.width - delta }
      }
    }
  }

  const pointerDelta = (event: React.PointerEvent, pointer: { x: number; y: number }) => ({
    dx: (event.clientX - pointer.x) / physicalZoom,
    dy: (event.clientY - pointer.y) / physicalZoom,
  })

  const startResize = (edge: ResizeEdge) => (event: React.PointerEvent<HTMLDivElement>) => {
    event.stopPropagation()
    event.preventDefault()
    ;(event.target as HTMLElement).setPointerCapture(event.pointerId)
    // Base the drag on what the user visually sees — if a prior resize is
    // still bridging (previewLayout set, props not yet updated), the visible
    // size is `previewLayout`, and the pointer math must be relative to that.
    resizeStart.current = {
      edge,
      pointer: { x: event.clientX, y: event.clientY },
      layout: previewLayout ?? swimlane.layoutOptions,
    }
    setActiveEdge(edge)
  }

  const moveResize = (event: React.PointerEvent<HTMLDivElement>): void => {
    if (!resizeStart.current) {
      return
    }
    const { edge, pointer, layout: start } = resizeStart.current
    const { dx, dy } = pointerDelta(event, pointer)
    setPreviewLayout(clampLayout(edge, start, dx, dy))
  }

  const endResize = (event: React.PointerEvent<HTMLDivElement>): void => {
    if (!resizeStart.current) {
      return
    }
    ;(event.target as HTMLElement).releasePointerCapture(event.pointerId)
    const { edge, pointer, layout: start } = resizeStart.current
    const { dx, dy } = pointerDelta(event, pointer)
    const clamped = clampLayout(edge, start, dx, dy)
    const finalLayout = {
      x: Math.round(clamped.x),
      y: Math.round(clamped.y),
      width: Math.round(clamped.width),
      height: Math.round(clamped.height),
    }
    resizeStart.current = null
    setActiveEdge(null)
    const changed = (['x', 'y', 'width', 'height'] as const).some(
      (key) => finalLayout[key] !== Math.round(start[key])
    )
    if (changed) {
      // Hold the committed layout in previewLayout until the resize service
      // round-trip lands: swimlane.layoutOptions from props stays stale until
      // SwimlaneEvents.swimlanesMovedInService fires post-HTTP, so clearing
      // preview here would flash the lane back to its pre-resize size.
      preResizeLayout.current = swimlane.layoutOptions
      setPreviewLayout(finalLayout)
      resizeLane(swimlane, finalLayout)
    } else {
      setPreviewLayout(null)
    }
  }

  const commitRename = (): void => {
    const newName = editingName?.trim()
    setEditingName(null)
    if (newName && newName !== swimlane.name) {
      renameLane(swimlane, newName)
    }
  }

  const actions = [
    {
      icon: 'swimlane_add_lane_above',
      label: 'Add Lane Above',
      testId: 'swimlane_add_lane_above',
      disabled: false,
      command: () => addLane(swimlane, DirectionVertical.TOP),
    },
    {
      icon: 'swimlane_add_lane_below',
      label: 'Add Lane Below',
      testId: 'swimlane_add_lane_below',
      disabled: false,
      command: () => addLane(swimlane, DirectionVertical.BOTTOM),
    },
    {
      icon: 'swimlane_move_lane_up',
      label: 'Move Lane Up',
      testId: 'swimlane_move_lane_up',
      disabled: laneIndex === 0,
      command: () => moveLane(swimlane, DirectionVertical.TOP),
    },
    {
      icon: 'swimlane_move_lane_below',
      label: 'Move Lane Down',
      testId: 'swimlane_move_lane_below',
      disabled: laneIndex === orderedLaneIds.length - 1,
      command: () => moveLane(swimlane, DirectionVertical.BOTTOM),
    },
    {
      icon: 'swimlane_delete_lane',
      label: 'Delete Lane',
      testId: 'swimlane_delete_lane',
      disabled: orderedLaneIds.length < 2,
      command: () => deleteLane(swimlane),
    },
  ]

  return (
    <div
      data-testid={`swimlane-${swimlane.id}`}
      style={{
        width,
        height,
        // Origin-moving previews (top/left edge) shift the lane box locally; member
        // nodes are RF children of the untouched wrapper, so they stay put — matching
        // the committed outcome (node coords get compensated by the resize service).
        transform: previewLayout
          ? `translate(${layout.x - swimlane.layoutOptions.x}px, ${layout.y - swimlane.layoutOptions.y}px)`
          : undefined,
        // Selected lane stroke: neutral_400 while selected, neutral_200 otherwise
        border: `2px solid ${selected ? GraphColors.neutral_400 : GraphColors.neutral_200}`,
        background: 'transparent',
        position: 'relative',
      }}
    >
      <div
        data-testid={`swimlane-label-band-${swimlane.id}`}
        role="button"
        tabIndex={0}
        onClick={(event) => {
          event.stopPropagation()
          if (editingName === null) {
            selectLane(selected ? null : swimlane.id)
          }
        }}
        onDoubleClick={(event) => {
          event.stopPropagation()
          if (!readonly) {
            setEditingName(swimlane.name)
          }
        }}
        onKeyDown={(event) => {
          if (editingName === null && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault()
            selectLane(selected ? null : swimlane.id)
          }
        }}
        style={{
          position: 'absolute',
          right: '100%',
          top: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 10px',
          pointerEvents: 'auto',
          cursor: 'pointer',
        }}
      >
        {editingName === null ? (
          <span
            style={{
              transform: 'rotate(180deg)',
              writingMode: 'vertical-rl',
              color: '#000',
              fontSize: 32,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxHeight: height * 0.8,
            }}
          >
            {swimlane.name}
          </span>
        ) : (
          <input
            ref={nameInputRef}
            data-testid={`swimlane-name-input-${swimlane.id}`}
            value={editingName}
            onChange={(event) => setEditingName(event.target.value)}
            onBlur={() => commitRename()}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => {
              event.stopPropagation()
              if (event.key === 'Enter') {
                commitRename()
              }
              if (event.key === 'Escape') {
                setEditingName(null)
              }
            }}
            style={{
              transform: 'rotate(180deg)',
              writingMode: 'vertical-rl',
              color: '#000',
              fontSize: 32,
              maxHeight: height * 0.8,
              width: 40,
              border: `1px solid ${GraphColors.blue_500}`,
              borderRadius: 4,
              outline: 'none',
              background: GraphColors.white,
            }}
          />
        )}
      </div>
      {!readonly && (
        <>
          {RESIZE_STRIPS.map(({ edge, style }) => (
            <div
              key={edge}
              data-testid={`swimlane-resize-handle-${edge}-${swimlane.id}`}
              onPointerEnter={() => setHoveredEdge(edge)}
              onPointerLeave={() =>
                setHoveredEdge((previous) => (previous === edge ? null : previous))
              }
              onPointerDown={startResize(edge)}
              onPointerMove={moveResize}
              onPointerUp={endResize}
              className="nodrag nopan"
              style={{
                position: 'absolute',
                pointerEvents: 'auto',
                background: activeEdge === edge ? GraphColors.blue_500_20 : 'transparent',
                ...style,
              }}
            />
          ))}
          {(activeEdge ?? hoveredEdge) && (
            <div
              data-testid={`swimlane-resize-indicator-${swimlane.id}`}
              style={{
                position: 'absolute',
                background: GraphColors.blue_500,
                pointerEvents: 'none',
                ...INDICATOR_STYLES[activeEdge ?? hoveredEdge],
              }}
            />
          )}
          {activeEdge && (
            <div
              data-testid={`swimlane-resize-badge-${swimlane.id}`}
              style={{
                position: 'absolute',
                background: GraphColors.white,
                border: `1px solid ${GraphColors.blue_500}`,
                borderRadius: 4,
                color: GraphColors.neutral_600,
                fontSize: 11,
                fontWeight: 600,
                padding: '2px 6px',
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
                ...badgePlacement(activeEdge, logicalZoom),
              }}
            >
              {activeEdge === 'left' || activeEdge === 'right'
                ? `${Math.round(width)} px`
                : `${Math.round(height)} px`}
            </div>
          )}
        </>
      )}
      {selected && !readonly && (
        <div
          data-testid={`swimlane-toolbar-${swimlane.id}`}
          style={{
            position: 'absolute',
            top: 8,
            left: '100%',
            marginLeft: 12,
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            pointerEvents: 'auto',
            transform: `scale(${1 / logicalZoom})`,
            transformOrigin: 'top left',
          }}
        >
          {actions.map((action) => (
            <SwimlaneActionButton
              key={action.testId}
              icon={action.icon}
              label={action.label}
              testId={action.testId}
              disabled={action.disabled}
              onClick={action.command}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default memo(SwimlaneNode)
